import type { ReactElement } from "react";
import type { QuickAccessParams } from "@playbookai/widget-schemas";
import type { DashboardWidgetEditorProps } from "../types.js";
import { useQuickAccessCatalog } from "./catalog.js";

export function QuickAccessEditor({
  draftParams,
  onChange,
}: DashboardWidgetEditorProps): ReactElement {
  const catalog = useQuickAccessCatalog();
  const params = draftParams as QuickAccessParams;
  const mode = params.mode ?? "all";
  const available = catalog?.modules ?? [];
  const saved = params.selectedModuleIds ?? [];
  const known = new Set(available.map((module) => module.id));
  const orderedKnown = saved.filter((id) => known.has(id));
  const modulesById = new Map(available.map((module) => [module.id, module]));
  const displayedModules = [
    ...orderedKnown.flatMap((id) => {
      const module = modulesById.get(id);
      return module ? [module] : [];
    }),
    ...available.filter((module) => !orderedKnown.includes(module.id)),
  ];
  const retainedUnknown = saved.filter((id) => !known.has(id));

  const changeMode = (nextMode: "all" | "selected"): void => {
    onChange({
      mode: nextMode,
      selectedModuleIds:
        nextMode === "selected" && orderedKnown.length === 0
          ? available.map((module) => module.id)
          : saved,
    });
  };
  const commitKnown = (nextKnown: readonly string[]): void => {
    onChange({
      mode: "selected",
      selectedModuleIds: [...nextKnown, ...retainedUnknown],
    });
  };

  return (
    <fieldset className="space-y-3">
      <legend className="mb-1.5 text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
        Apps shown
      </legend>
      <label className="flex items-center gap-2 text-sm text-pb-text">
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a native radio; RadioGroup draws its own teal mark, and no approved look row lists the change. */}
        <input
          type="radio"
          name="quick-access-mode"
          checked={mode === "all"}
          onChange={() => changeMode("all")}
        />
        All enabled apps
      </label>
      <label className="flex items-center gap-2 text-sm text-pb-text">
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a native radio; RadioGroup draws its own teal mark, and no approved look row lists the change. */}
        <input
          type="radio"
          name="quick-access-mode"
          disabled={available.length === 0}
          checked={mode === "selected"}
          onChange={() => changeMode("selected")}
        />
        Selected apps
      </label>
      {mode === "selected" ? (
        <ul className="divide-y divide-pb-border/60 rounded-md border border-pb-border">
          {displayedModules.map((module) => {
            const selected = orderedKnown.includes(module.id);
            const index = orderedKnown.indexOf(module.id);
            return (
              <li
                key={module.id}
                className="flex items-center gap-2 px-2 py-1.5"
              >
                <label className="flex min-w-0 flex-1 items-center gap-2 text-sm text-pb-text">
                  {/* eslint-disable-next-line ds/no-hand-built -- kept: a native checkbox; Checkbox draws its own teal box, and no approved look row lists the change. */}
                  <input
                    type="checkbox"
                    checked={selected}
                    disabled={
                      selected &&
                      orderedKnown.length === 1 &&
                      retainedUnknown.length === 0
                    }
                    onChange={() =>
                      commitKnown(
                        selected
                          ? orderedKnown.filter((id) => id !== module.id)
                          : [...orderedKnown, module.id],
                      )
                    }
                  />
                  <span className="truncate">{module.name}</span>
                </label>
                {selected ? (
                  <span className="flex gap-1">
                    {/* eslint-disable-next-line ds/no-hand-built -- kept: a 12px muted text step control; Button xs is a 24px box, and no approved look row lists the change. */}
                    <button
                      type="button"
                      aria-label={`Move ${module.name} up`}
                      disabled={index === 0}
                      onClick={() => {
                        const next = [...orderedKnown];
                        const [moved] = next.splice(index, 1);
                        if (moved === undefined) return;
                        next.splice(index - 1, 0, moved);
                        commitKnown(next);
                      }}
                      className="text-xs text-pb-text-muted disabled:opacity-40"
                    >
                      Up
                    </button>
                    {/* eslint-disable-next-line ds/no-hand-built -- kept: a 12px muted text step control; Button xs is a 24px box, and no approved look row lists the change. */}
                    <button
                      type="button"
                      aria-label={`Move ${module.name} down`}
                      disabled={index === orderedKnown.length - 1}
                      onClick={() => {
                        const next = [...orderedKnown];
                        const [moved] = next.splice(index, 1);
                        if (moved === undefined) return;
                        next.splice(index + 1, 0, moved);
                        commitKnown(next);
                      }}
                      className="text-xs text-pb-text-muted disabled:opacity-40"
                    >
                      Down
                    </button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </fieldset>
  );
}
